import { useEffect, useMemo } from "react";
import { Chip } from "../../../../core/components/atoms/Chip";
import { useFetchSubareaInstitutions } from "../../../../core/hooks/useFetchSubareaInstitutions";
import { useExerciseStore } from "../../stores/exercisesStore";
import { useFilterConfigStore } from "../../stores/filterConfig";
import { useNewSubjectStore } from "../../stores/newSubjectStore";
import { useParams } from "react-router-dom";

interface IFilterTagsList {
  origin?: string;
  onFilterChange?: () => void;
}

const FilterTagsList: React.FC<IFilterTagsList> = ({ origin = 'modal', onFilterChange }) => {
  const { module } = useParams();

  const { 
    type, 
    setType,
    status, 
    setStatus,
    favorites,
    setFavorites,
    resetFilters,
    incidence,
    setIncidence
  } = useFilterConfigStore();

  const {
    favorite,
    setFavorite,
    selectedYear,
    setSelectedYear,
    unanswered,
    setUnanswered,
    rightAnswer,
    setRightAnswer,
    wrongAnswer,
    setWrongAnswer,
    difficulty,
    setDifficulty,
    questionOptions,
    setQuestionOptions,
    isEnem,
    setIsEnem,
    institutionType,
    setInstitutionType,
    clearFilters
  } = useExerciseStore();

  const { selectedSubjects, setSelectedSubjects, selectOptions, module: selectedModule } = useNewSubjectStore();

  // Converte isEnem para exerciseType
  const exerciseType = useMemo(() => {
    const hasEnem = isEnem.includes('enem');
    const hasVestibulares = isEnem.includes('vestibulares');
    if (hasEnem && !hasVestibulares) return 'enem';
    if (hasVestibulares && !hasEnem) return 'vest';
    return null;
  }, [isEnem]);

  const fetchInstitutions = useFetchSubareaInstitutions({
    slug: module,
    selectedYear,
    exerciseType,
    difficulty,
    questionOptions,
    favorite,
    unanswered,
    rightAnswer,
    wrongAnswer,
    selectedSubjects,
    enabled: institutionType.length > 0,
  });

  const institutionNames = useMemo(() => {
    if (institutionType.length === 0 || !fetchInstitutions.data) return [];
    return institutionType.map((type) => {
      const institution = fetchInstitutions.data.find(
        (item: { label: string; value: string }) => String(item.value) === type
      );
      return institution?.label ?? null;
    }).filter(Boolean);
  }, [institutionType, fetchInstitutions.data])

  useEffect(() => {
    if (origin === 'main' && (selectedModule !== module || !module)) {
      resetFilters();
      clearFilters();
    }
  }, [module, origin])

  const removeSubject = (slug: string) => {
    const auxArray = [...selectedSubjects];
    const newArray = auxArray.filter((it) => it !== slug);

    setSelectedSubjects(newArray);
    onFilterChange?.();
  }

  const removeDificulty = (slug: string) => {
    const auxArray = [...difficulty];
    const newArray = auxArray.filter((it) => it !== slug);

    setDifficulty(newArray);
    onFilterChange?.();
  }

  const removeType = (slug: string) => {
    const auxArray = [...isEnem];
    const newArray = auxArray.filter((it) => it !== slug);

    setIsEnem(newArray);
    onFilterChange?.();
  }

  const removeQuestionOption = (slug: string) => {
    const auxArray = [...questionOptions];
    const newArray = auxArray.filter((it) => it !== slug);

    setQuestionOptions(newArray);
    onFilterChange?.();
  }

  const removeYear = (year: string) => {
    const newArray = selectedYear.filter((it) => it !== year);
    setSelectedYear(newArray);
    onFilterChange?.();
  }

  const removeInstitution = (institution: string) => {
    const newArray = institutionType.filter((it) => it !== institution);
    setInstitutionType(newArray);
    onFilterChange?.();
  }

  const FilterChip = ({ label, onRemove, hastRemove }: { label: string, onRemove: () => void; hastRemove?: boolean; }) => (
    <Chip leftIcon={hastRemove ? undefined : { type: 'material', Icon: 'close', onClick: () => onRemove()}}>
      {label}
    </Chip>
  )

  const statusOptions = (status: string) => {
    switch(status) {
      case 'played':
        return 'Reproduzidos'
      case 'not_played':
        return 'Não reproduzidos'
      case 'seen':
        return 'Visualizados'
      case 'not_seen':
        return 'Não visualizados'
      case 'listened':
        return 'Reproduzidos'
      case 'not_listened':
        return 'Não reproduzidos'
      default:
        return ''
    }
  }

  const difficultiesOptions = (difficulty: string) => {
    switch(difficulty) {
      case 'easy':
        return 'Fácil'
      case 'medium':
        return 'Médio'
      case 'hard':
        return 'Difícil'
      case 'random':
        return 'Aleatórios'
      default: 
        return ''
    }
  }

  const questionOptionsNames = (option: string) => {
    switch(option) {
      case 'single_answer':
        return 'Resposta única'
      case 'multiple_answer':
        return 'Resposta somatória'
      case 'discursive':
        return 'Resposta discursiva'
      default: 
        return '-'
    }
  }

  const exerciseTypeNames = (option: string) => {
    switch(option) {
      case 'enem':
        return 'Enem'
      case 'vestibulares':
        return 'Vestibulares'
      default: 
        return '-'
    }
  }

  const incidenceOptions = (incidence: number) => {
    switch(incidence) {
      case 1:
        return 'Incidência 1'
      case 2:
        return 'Incidência 2'
      case 3:
        return 'Incidência 3'
      case 4:
        return 'Incidência 4'
      case 5:
        return 'Incidência 5'
      default:
        return '-'
    }
  }
  return (
    <div id="subjects-scroll" className='flex flex-row gap-md overflow-x-auto h-fit'>
      {selectedSubjects.map((it, i) => 
        <FilterChip 
          key={i} 
          label={selectOptions.find((item) => item.value === it)?.label ?? ''}
          onRemove={() => removeSubject(it)}
        />
      )}
      {type.length > 0 && type.map((it, i) =>
        <FilterChip
          key={i}
          label={it === 'main' ? 'Principal' : 'Resumo'}
          onRemove={() => { setType(it); onFilterChange?.(); }}
          hastRemove={origin === 'main'}
        />
      )}
      {status.length > 0 && status.map((it, i) =>
        <FilterChip
          key={i}
          label={statusOptions(it)}
          onRemove={() => { setStatus(it); onFilterChange?.(); }}
          hastRemove={origin === 'main'}
        />
      )}
      {favorites && <FilterChip label="Favoritos" onRemove={() => { setFavorites(!favorites); onFilterChange?.(); }} hastRemove={false} />}
      {favorite && <FilterChip label="Favoritos" onRemove={() => { setFavorite(!favorite); onFilterChange?.(); }} hastRemove={false} />}
      {unanswered && <FilterChip label="Não respondidos" onRemove={() => { setUnanswered(!unanswered); onFilterChange?.(); }} hastRemove={false} />}
      {rightAnswer && <FilterChip label="Respostas certas" onRemove={() => { setRightAnswer(!rightAnswer); onFilterChange?.(); }} hastRemove={false} />}
      {wrongAnswer && <FilterChip label="Respostas erradas" onRemove={() => { setWrongAnswer(!wrongAnswer); onFilterChange?.(); }} hastRemove={false} />}
      {difficulty.length > 0 && difficulty.map((it, i) => 
        <FilterChip key={i} label={difficultiesOptions(it)} onRemove={() => removeDificulty(it)} hastRemove={false} />
      )}
      {isEnem.length > 0 && isEnem.map((it, i) => 
        <FilterChip key={i} label={exerciseTypeNames(it)} onRemove={() => removeType(it)} hastRemove={false} />
      )}
      {questionOptions.length > 0 && questionOptions.map((it, i) => 
        <FilterChip key={i} label={questionOptionsNames(it)} onRemove={() => removeQuestionOption(it)} hastRemove={false} />
      )}
      {selectedYear.length > 0 && selectedYear.map((year, i) =>
        <FilterChip key={`year-${i}`} label={year} onRemove={() => removeYear(year)} hastRemove={false} />
      )}
      {institutionNames.length > 0 && institutionNames.map((name, i) =>
        <FilterChip key={`inst-${i}`} label={name as string} onRemove={() => removeInstitution(institutionType[i])} hastRemove={false} />
      )}
      {incidence.length > 0 && incidence.map((it, i) => 
        <FilterChip key={i} label={incidenceOptions(it)} onRemove={() => setIncidence(it)} hastRemove={origin === 'main'} />
      )}
    </div>
  )
}

export default FilterTagsList;